<template>
  <div class="substep-pick-projects">
    <div class="substep-header">
      <h3 data-testId="step-title">Pick Projects</h3>
      <p>Your team’s time needs to be kept organized. Create the projects your team can track time towards.</p>
    </div>
    <div class="substep-body">
      <div class="projects">
        <div
          v-for="option in projects"
          :key="option.value"
          class="option"
          :class="{ selected: isSelected(option.value) }"
          @click="toggleOption(option.value)"
        >
          <input
            v-model="selectedOptions"
            type="checkbox"
            :value="option.value"
          />
          <i
            v-if="!isSelected(option.value)"
            class="hi hi-plus hi-12"
          ></i>
          {{ truncateName(option.name) }}
          <i
            v-if="isSelected(option.value) && !option.id"
            class="hi hi-close hi-12"
          ></i>
        </div>
        <div
          v-if="!showCreateProjectInput && !projectLimitReached"
          class="option selected"
          @click="toggleCreateProject"
        >
          <i class="hi hi-plus hi-12"></i>
          Create your project
        </div>
      </div>
      <div
        v-if="showCreateProjectInput && !projectLimitReached"
        class="create-project-input"
      >
        <AppInput
          v-model="newProjectName"
          type="text"
          placeholder="Enter project name"
          maxlength="256"
        />

        <AppButton
          type="primary"
          block
          @click="addProject"
        >
          Add
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import AppButton from '~/components/AppButton.vue';
import AppInput from '~/components/AppInput.vue';
import { ProjectsResource } from '~/resources';

export default {
  name: 'PickProjects',
  components: {
    AppButton,
    AppInput,
  },
  mixins: [GettingStartedMixin],
  props: {
    projects: {
      type: Array,
      default: () => [],
      validator: (value) => {
        const requiredAttributes = ['value', 'name', 'selected'];
        const hasRequiredAttributes = (project) => {
          const projectKeys = Object.keys(project);

          return requiredAttributes.every((attr) => projectKeys.includes(attr));
        };

        return value.every((project) => hasRequiredAttributes(project));
      },
    },
    organization: {
      type: Object,
      required: true,
      validator: (value) => {
        const valueKeys = Object.keys(value);

        return valueKeys.includes('id');
      },
    },
    currentUser: {
      type: Object,
      required: true,
      validator: (value) => {
        const valueKeys = Object.keys(value);

        return valueKeys.includes('id');
      },
    },
  },
  data() {
    return {
      selectedOptions: [],
      showCreateProjectInput: false,
      newProjectName: '',
      hasCompletedSubstep: false,
      projectLimit: 25,
      projectLimitReached: false,
    };
  },
  watch: {
    selectedOptions: {
      handler() {
        this.checkSelectedOptions();
      },
      deep: true,
    },
    projects: {
      immediate: true,
      handler(newProjects) {
        this.checkProjectCount(newProjects);
        this.initializeSelectedOptions(newProjects);
      },
    },
  },
  created() {
    this.$parent.$on('userClickedContinue', this.handleFinishedSubstep);
  },
  methods: {
    initializeSelectedOptions(projects) {
      this.selectedOptions = this.projects.filter((option) => option.selected).map((option) => option.value);
    },
    checkProjectCount(projects) {
      if (projects.length > this.projectLimit) {
        this.projectLimitReached = true;
      }
    },
    async handleFinishedSubstep() {
      await this.createProjects();
      this.$emit('finishSubstep');
    },
    isSelected(value) {
      return this.selectedOptions.includes(value);
    },
    toggleOption(value) {
      const index = this.selectedOptions.indexOf(value);
      const project = this.projects.find((option) => option.value === value);

      if (project && project.id) {
        return;
      }

      if (index === -1) {
        this.selectedOptions.push(value);
        if (project) {
          project.selected = true;
        }
      } else {
        this.selectedOptions.splice(index, 1);
        if (project) {
          project.selected = false;
        }
      }
    },
    toggleCreateProject() {
      this.showCreateProjectInput = !this.showCreateProjectInput;
    },
    addProject() {
      const trimmedProjectName = this.newProjectName.trim();

      if (!trimmedProjectName) {
        return;
      }

      const existingProject = this.projects.find((project) => project.value === trimmedProjectName);

      if (!existingProject) {
        // eslint-disable-next-line vue/no-mutating-props
        this.projects.push({ value: trimmedProjectName, name: trimmedProjectName, selected: true });
      }

      if (!this.selectedOptions.includes(trimmedProjectName)) {
        this.selectedOptions.push(trimmedProjectName);
      }

      this.newProjectName = '';
      this.showCreateProjectInput = false;
    },
    checkSelectedOptions() {
      if (this.selectedOptions.length > 0 && !this.hasCompletedSubstep) {
        this.$emit('completeSubstep');
        this.hasCompletedSubstep = true;
      } else if (this.selectedOptions.length === 0 && this.hasCompletedSubstep) {
        this.$emit('incompleteSubstep');
        this.hasCompletedSubstep = false;
      }
    },
    async createProjects() {
      const params = {
        skip_existing_project: true,
        projects: this.selectedOptions.map((option) => ({
          name: option,
          members: [{ user_id: this.currentUser.id, role: 'manager', selected: true }],
        })),
      };

      try {
        const response = await ProjectsResource.create({ id: this.organization.id }, params);

        if (response.success) {
          this.$appMessage('Projects created successfully');
        } else if (response.errors) {
          this.$appMessage('Something went wrong. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      }
    },
    truncateName(name) {
      return name.length > 50 ? name.slice(0, 47) + '...' : name;
    },
  },
};
</script>
